import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CustomAchievement {
  id: string;
  type: string;
  badge: string;
  name: string;
  description: string;
  requirement: string;
  points: number;
  category: string;
  is_permanent: boolean;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomAchievementInput {
  type: string;
  badge: string;
  name: string;
  description: string;
  requirement: string;
  points: number;
  category: string;
  is_permanent: boolean;
  is_active?: boolean;
}

export const useCustomAchievements = () => {
  const { user } = useAuth();
  const [customAchievements, setCustomAchievements] = useState<CustomAchievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomAchievements = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('custom_achievements')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setCustomAchievements(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching custom achievements:', err);
      setError('Error al cargar logros personalizados');
    } finally {
      setLoading(false);
    }
  }, []);

  const createAchievement = useCallback(async (input: CustomAchievementInput) => {
    if (!user) return { success: false, error: 'No autenticado' };

    try {
      const { error: insertError } = await supabase
        .from('custom_achievements')
        .insert({
          ...input,
          created_by: user.id,
          is_active: input.is_active ?? true,
        });

      if (insertError) throw insertError;
      await fetchCustomAchievements();
      return { success: true };
    } catch (err: any) {
      console.error('Error creating achievement:', err);
      return { success: false, error: err.message || 'Error al crear logro' };
    }
  }, [user, fetchCustomAchievements]);

  const updateAchievement = useCallback(async (id: string, input: Partial<CustomAchievementInput>) => {
    try {
      const { error: updateError } = await supabase
        .from('custom_achievements')
        .update(input)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchCustomAchievements();
      return { success: true };
    } catch (err: any) {
      console.error('Error updating achievement:', err);
      return { success: false, error: err.message || 'Error al actualizar logro' };
    }
  }, [fetchCustomAchievements]);

  const deleteAchievement = useCallback(async (id: string) => {
    try {
      // First, delete any seller_achievements that reference this custom achievement
      const achievement = customAchievements.find(a => a.id === id);
      if (achievement) {
        await supabase
          .from('seller_achievements')
          .delete()
          .eq('achievement_type', achievement.type);
      }

      const { error: deleteError } = await supabase
        .from('custom_achievements')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchCustomAchievements();
      return { success: true };
    } catch (err: any) {
      console.error('Error deleting achievement:', err);
      return { success: false, error: err.message || 'Error al eliminar logro' };
    }
  }, [fetchCustomAchievements, customAchievements]);

  const toggleActive = useCallback(async (id: string, isActive: boolean) => {
    return updateAchievement(id, { is_active: isActive });
  }, [updateAchievement]);

  useEffect(() => {
    fetchCustomAchievements();
  }, [fetchCustomAchievements]);

  return {
    customAchievements,
    activeCustomAchievements: customAchievements.filter(a => a.is_active),
    loading,
    error,
    createAchievement,
    updateAchievement,
    deleteAchievement,
    toggleActive,
    refetch: fetchCustomAchievements,
  };
};

export default useCustomAchievements;
